import { useCallback, useEffect, useState } from 'react'
import { Spin, Tooltip } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useBackToParent, usePersistentPageSize } from '@/hooks'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { isHandledAuthError } from '@/services/client'
import { ocrSuccessStatsApi } from '@/services/ocrSuccessStats/api'
import type { OcrSuccessStatsRecord } from '@/services/ocrSuccessStats/types'
import OcrMonthlyUsageView from './OcrMonthlyUsageView'
import { BackButton, DemoInput, DownloadIcon, Pagination, ResizeHandle, ResultCard, SearchCard, SortableHeader, TableLoadingRow } from '@/widgets/demo/ListPrimitives'
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl'
import { computePagination, demoButtonClass, useResizableColumns, useSortState, type ColumnWidthMap, type SortState } from '@/widgets/demo/listHelpers'

const COLUMN_DEFAULTS: ColumnWidthMap = {
  municipalityId: 96,
  municipalityCode: 128,
  municipalityName: 180,
  importNo: 152,
  docType: 104,
  identId: 132,
  householdIdentCode: 132,
  ocrParsedAt: 168,
  ocrApiId: 152,
  pdfFileName: 240,
  pdfPageCount: 96,
}

const COLUMN_ORDER = [
  'municipalityId',
  'municipalityCode',
  'municipalityName',
  'importNo',
  'docType',
  'identId',
  'householdIdentCode',
  'ocrParsedAt',
  'ocrApiId',
  'pdfFileName',
  'pdfPageCount',
] as const

// 列表示設定（No.30 横展開）。先頭の主キー相当の自治体ID(municipalityId)は常に表示にして、
// 行の同定ができなくなる事故を防ぐ。選択/操作列は無い。それ以外は切替可。
const OCR_COLUMN_VISIBILITY_KEY = 'ocr-success-stats'
const OCR_ALWAYS_VISIBLE_COLUMNS = ['municipalityId']

// 列表示設定の列ラベル（一覧ヘッダの表示テキストと揃える）。
const OCR_COLUMN_LABELS: Record<(typeof COLUMN_ORDER)[number], string> = {
  municipalityId: '自治体ID',
  municipalityCode: '自治体コード',
  municipalityName: '自治体名',
  importNo: '取込番号',
  docType: '文書種別',
  identId: '識別ID',
  householdIdentCode: '世帯識別',
  ocrParsedAt: 'OCR解析日時',
  ocrApiId: 'OCR API ID',
  pdfFileName: 'PDFファイル名',
  pdfPageCount: 'PDFページ数',
}

const OCR_COLUMN_VISIBILITY_ITEMS = COLUMN_ORDER.map((id) => ({
  id,
  label: OCR_COLUMN_LABELS[id],
}))

interface FilterState {
  municipalityCode: string
  municipalityName: string
  importNo: string
  identId: string
  householdIdentCode: string
  fileName: string
  dateFrom: string
  dateTo: string
}

function toDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function defaultDateRange(): Pick<FilterState, 'dateFrom' | 'dateTo'> {
  const today = new Date()
  const from = new Date(today)
  from.setMonth(from.getMonth() - 1)
  return { dateFrom: toDateInputValue(from), dateTo: toDateInputValue(today) }
}

// 月次モードの年月ピッカー用。'YYYY-MM' を、共通の絞り込み state が持つ 'YYYY-MM-DD' 形式へ
// 月初 / 月末で展開する（バックエンドは日付を受けるため型は維持しつつ、月まるごとの範囲にする）。
function monthStart(ym: string): string {
  return ym ? `${ym}-01` : ''
}
function monthEnd(ym: string): string {
  if (!ym) return ''
  const [y, m] = ym.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return `${ym}-${String(lastDay).padStart(2, '0')}`
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function formatDownloadStamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
}

const OcrSuccessStats = () => {
  const goBackToParent = useBackToParent()
  const defaultDates = defaultDateRange()
  const defaultFilter: FilterState = {
    municipalityCode: '',
    municipalityName: '',
    importNo: '',
    identId: '',
    householdIdentCode: '',
    fileName: '',
    ...defaultDates,
  }
  const [savedFilter, setSavedFilter] = useSearchPersistence<FilterState>('ocr-success-stats', defaultFilter)
  // page / sort は FilterState に含めず別キーで最小限に永続化する（既存の filter 永続化はそのまま）。
  const [savedPage, setSavedPage] = useSearchPersistence<number>('ocr-success-stats:page', 1)
  const [savedSort, setSavedSort] = useSearchPersistence<SortState>('ocr-success-stats:sort', { sortBy: null, order: null })
  // 表示モード（明細一覧 / 月次集計）。既定は従来どおり明細一覧。
  const [mode, setMode] = useSearchPersistence<'detail' | 'monthly'>('ocr-success-stats:mode', 'detail')
  // 月次ビュー（子コンポーネント）の再マウント用キー。月次の page は子側の内部stateのため、
  // 検索/クリアの度に +1 して子を作り直し、確実に先頭ページ（page=1）の新鮮な状態へ戻す。
  const [monthlyReloadKey, setMonthlyReloadKey] = useState(0)
  const initialFilter: FilterState = {
    ...defaultFilter,
    ...savedFilter,
    dateFrom: savedFilter.dateFrom || defaultDates.dateFrom,
    dateTo: savedFilter.dateTo || defaultDates.dateTo,
  }

  const [qMunicipalityCode, setQMunicipalityCode] = useState(initialFilter.municipalityCode)
  const [qMunicipalityName, setQMunicipalityName] = useState(initialFilter.municipalityName)
  const [qImportNo, setQImportNo] = useState(initialFilter.importNo)
  const [qIdentId, setQIdentId] = useState(initialFilter.identId)
  const [qHouseholdIdentCode, setQHouseholdIdentCode] = useState(initialFilter.householdIdentCode)
  const [qFileName, setQFileName] = useState(initialFilter.fileName)
  const [qDateFrom, setQDateFrom] = useState(initialFilter.dateFrom)
  const [qDateTo, setQDateTo] = useState(initialFilter.dateTo)
  const [applied, setApplied] = useState<FilterState>(initialFilter)
  const [rows, setRows] = useState<OcrSuccessStatsRecord[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null)
  const [page, setPage] = useState(savedPage ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('ocr-success-stats')
  const { sort, setSort, sortQuery } = useSortState(savedSort)
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: COLUMN_DEFAULTS })

  // 列表示設定（No.30 横展開）。表示中の列だけに絞った順序配列を導出し、colgroup/thead/tbody/幅計算を統一する。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(OCR_COLUMN_VISIBILITY_KEY, COLUMN_ORDER, {
      alwaysVisible: OCR_ALWAYS_VISIBLE_COLUMNS,
    })
  const visibleColumnOrder = COLUMN_ORDER.filter((id) => isColVisible(id))
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0)
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1

  const load = useCallback(async () => {
    // 月次集計モードでは明細一覧を取得しない（月次ビューが自前で取得する）。
    if (mode !== 'detail') return
    setLoading(true)
    try {
      const { data } = await ocrSuccessStatsApi.list({
        municipalityCode: applied.municipalityCode.trim() || undefined,
        municipalityName: applied.municipalityName.trim() || undefined,
        importNo: applied.importNo.trim() || undefined,
        identId: applied.identId.trim() || undefined,
        householdIdentCode: applied.householdIdentCode.trim() || undefined,
        fileName: applied.fileName.trim() || undefined,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        page: page - 1,
        size: pageSize,
        ...sortQuery,
      })
      const content = data.records?.content ?? []
      const total = data.records?.totalElements ?? 0
      // 取得ページが範囲外（永続化された page が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (content.length === 0 && total > 0 && page > 1) {
        const lastPage = Math.max(1, Math.ceil(total / pageSize))
        setTotalElements(total)
        setPage(lastPage)
        return
      }
      setRows(content)
      setTotalElements(total)
    } catch (error) {
      setRows([])
      setTotalElements(0)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [applied, page, pageSize, sortQuery, mode])

  useEffect(() => {
    // 検索条件・ページ変更に応じて集計データを取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  // 現在のページ・並び順を別キーで永続化し、再訪問時に復元する。
  useEffect(() => {
    setSavedPage(page)
    setSavedSort(sort)
  }, [page, sort, setSavedPage, setSavedSort])

  const runSearch = () => {
    // 明細のローディング表示は明細モード時のみ点灯（月次時は load() が早期returnし消灯しないため）。
    if (mode === 'detail') setLoading(true)
    setPage(1)
    // 月次ビューを先頭ページから引き直す（子コンポーネント再マウント）。
    setMonthlyReloadKey((k) => k + 1)
    const nextFilter: FilterState = {
      municipalityCode: qMunicipalityCode,
      municipalityName: qMunicipalityName,
      importNo: qImportNo,
      identId: qIdentId,
      householdIdentCode: qHouseholdIdentCode,
      fileName: qFileName,
      dateFrom: qDateFrom,
      dateTo: qDateTo,
    }
    setApplied(nextFilter)
    setSavedFilter(nextFilter)
  }

  const runClear = () => {
    if (mode === 'detail') setLoading(true)
    setMonthlyReloadKey((k) => k + 1)
    const dates = defaultDateRange()
    const nextFilter: FilterState = {
      municipalityCode: '',
      municipalityName: '',
      importNo: '',
      identId: '',
      householdIdentCode: '',
      fileName: '',
      ...dates,
    }
    setQMunicipalityCode('')
    setQMunicipalityName('')
    setQImportNo('')
    setQIdentId('')
    setQHouseholdIdentCode('')
    setQFileName('')
    setQDateFrom(dates.dateFrom)
    setQDateTo(dates.dateTo)
    setPage(1)
    setApplied(nextFilter)
    setSavedFilter(nextFilter)
  }

  const handleSortChange = (next: typeof sort) => {
    setSort(next)
    setPage(1)
  }

  const handleCsvDownload = async () => {
    setDownloadLoading('OCR成功統計をダウンロードしています...')
    try {
      const resp = await ocrSuccessStatsApi.exportCsv({
        municipalityCode: applied.municipalityCode.trim() || undefined,
        municipalityName: applied.municipalityName.trim() || undefined,
        importNo: applied.importNo.trim() || undefined,
        identId: applied.identId.trim() || undefined,
        householdIdentCode: applied.householdIdentCode.trim() || undefined,
        fileName: applied.fileName.trim() || undefined,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        ...sortQuery,
      })
      const blob = new Blob([resp.data], { type: 'text/csv; charset=UTF-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `OCR成功統計_${formatDownloadStamp(new Date())}.csv`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setDownloadLoading(null)
    }
  }

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, totalElements, pageSize)

  return (
    <div className="min-h-screen bg-gray-100">
      {downloadLoading && <Spin fullscreen tip={downloadLoading} />}
      <SystemHeader breadcrumbs={[{ label: 'システム管理', page: 'submenu-admin' }, { label: 'OCR成功数統計' }]} />
      <main className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        {/* 表示モード切替（明細一覧 / 月次集計）。戻るリンクとは段を分け、独立したセグメント切替として見せる。 */}
        <div className="mb-4 mt-1">
          <div className="inline-flex items-center gap-1 rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setMode('detail')}
              className={`rounded-lg px-5 py-2 text-sm font-medium transition ${mode === 'detail' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              明細一覧
            </button>
            <button
              type="button"
              onClick={() => setMode('monthly')}
              className={`rounded-lg px-5 py-2 text-sm font-medium transition ${mode === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              月次集計
            </button>
          </div>
        </div>

        <SearchCard className="mb-4">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">自治体コード</label>
              <DemoInput value={qMunicipalityCode} onChange={(e) => setQMunicipalityCode(e.target.value)} className="w-full" placeholder="自治体コード（6桁）" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">自治体名</label>
              <DemoInput value={qMunicipalityName} onChange={(e) => setQMunicipalityName(e.target.value)} className="w-full" />
            </div>
            {mode === 'detail' && (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">取込番号</label>
                  <DemoInput value={qImportNo} onChange={(e) => setQImportNo(e.target.value)} className="w-full" placeholder="例: OCR-202605-000001" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">識別ID</label>
                  <DemoInput value={qIdentId} onChange={(e) => setQIdentId(e.target.value)} className="w-full" placeholder="SID-U-000001" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">世帯識別</label>
                  <DemoInput value={qHouseholdIdentCode} onChange={(e) => setQHouseholdIdentCode(e.target.value)} className="w-full" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">PDFファイル名</label>
                  <DemoInput value={qFileName} onChange={(e) => setQFileName(e.target.value)} className="w-full" />
                </div>
              </>
            )}
            {mode === 'monthly' ? (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">対象年月（From）</label>
                  <DemoInput type="month" value={qDateFrom.slice(0, 7)} onChange={(e) => setQDateFrom(monthStart(e.target.value))} className="w-full" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">対象年月（To）</label>
                  <DemoInput type="month" value={qDateTo.slice(0, 7)} onChange={(e) => setQDateTo(monthEnd(e.target.value))} className="w-full" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">OCR解析日（From）</label>
                  <DemoInput type="date" value={qDateFrom} onChange={(e) => setQDateFrom(e.target.value)} className="w-full" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">OCR解析日（To）</label>
                  <DemoInput type="date" value={qDateTo} onChange={(e) => setQDateTo(e.target.value)} className="w-full" />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={runClear} className={demoButtonClass.secondary}>クリア</button>
            <button type="button" onClick={runSearch} className={demoButtonClass.primary}>検索</button>
          </div>
        </SearchCard>

        {mode === 'detail' && (
        <ResultCard
          title={<h3 className="text-lg font-bold text-gray-800">OCR成功記録</h3>}
          extra={
            <>
              <span className="text-sm text-gray-500">検索結果 {totalElements} 件</span>
              <ColumnVisibilityControl
                columns={OCR_COLUMN_VISIBILITY_ITEMS}
                isVisible={isColVisible}
                setVisible={setColVisible}
                onReset={resetCols}
                alwaysVisible={OCR_ALWAYS_VISIBLE_COLUMNS}
                hiddenCount={hiddenColCount}
              />
              <button type="button" onClick={handleCsvDownload} className={demoButtonClass.slate}>
                <DownloadIcon />
                CSVダウンロード
              </button>
            </>
          }
        >
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50 whitespace-nowrap">
                <tr>
                  {isColVisible('municipalityId') && (
                    <SortableHeader field="municipalityId" label="自治体ID" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="municipalityId" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('municipalityCode') && (
                    <SortableHeader field="municipalityCode" label="自治体コード" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="municipalityCode" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('municipalityName') && (
                    <SortableHeader field="municipalityName" label="自治体名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="municipalityName" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('importNo') && (
                    <SortableHeader field="importNo" label="取込番号" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="importNo" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('docType') && (
                    <SortableHeader field="docType" label="文書種別" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="docType" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('identId') && (
                    <SortableHeader field="identId" label="識別ID" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="identId" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('householdIdentCode') && (
                    <SortableHeader field="householdIdentCode" label="世帯識別" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="householdIdentCode" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('ocrParsedAt') && (
                    <SortableHeader field="ocrParsedAt" label="OCR解析日時" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="ocrParsedAt" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('ocrApiId') && (
                    <SortableHeader field="ocrApiId" label="OCR API ID" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="ocrApiId" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('pdfFileName') && (
                    <SortableHeader field="pdfFileName" label="PDFファイル名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="pdfFileName" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('pdfPageCount') && (
                    <SortableHeader field="pdfPageCount" label="PDFページ数" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="pdfPageCount" onMouseDown={startResize} />} />
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={visibleColSpan} className="px-4 py-8 text-center text-sm text-gray-500">該当するデータがありません</td>
                    </tr>
                  )
                ) : (
                  rows.map((row, index) => (
                    <tr key={`${row.importNo}-${row.ocrApiId}-${index}`} className="hover:bg-blue-50">
                      {isColVisible('municipalityId') && (
                        <td className="px-2 py-3 text-sm tabular-nums truncate">{row.municipalityId}</td>
                      )}
                      {isColVisible('municipalityCode') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.municipalityCode || '－'} placement="topLeft"><span className="block truncate">{row.municipalityCode || '－'}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('municipalityName') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.municipalityName || '－'} placement="topLeft"><span className="block truncate">{row.municipalityName || '－'}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('importNo') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.importNo} placement="topLeft"><span className="block truncate">{row.importNo}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('docType') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.docType ?? '—'} placement="topLeft"><span className="block truncate">{row.docType ?? '—'}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('identId') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.identId ?? '—'} placement="topLeft"><span className="block truncate">{row.identId ?? '—'}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('householdIdentCode') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.householdIdentCode ?? '—'} placement="topLeft"><span className="block truncate">{row.householdIdentCode ?? '—'}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('ocrParsedAt') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={formatDateTime(row.ocrParsedAt)} placement="topLeft"><span className="block truncate">{formatDateTime(row.ocrParsedAt)}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('ocrApiId') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.ocrApiId} placement="topLeft"><span className="block truncate">{row.ocrApiId}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('pdfFileName') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={row.pdfFileName ?? '—'} placement="topLeft"><span className="block truncate">{row.pdfFileName ?? '—'}</span></Tooltip>
                        </td>
                      )}
                      {isColVisible('pdfPageCount') && (
                        <td className="px-2 py-3 text-sm tabular-nums truncate">{row.pdfPageCount ?? '—'}</td>
                      )}
                      <td aria-hidden="true" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={totalElements}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setPageSize(next); setPage(1) }}
          />
        </ResultCard>
        )}

        {mode === 'monthly' && (
          <OcrMonthlyUsageView key={monthlyReloadKey} applied={applied} />
        )}
      </main>
    </div>
  )
}

export default OcrSuccessStats
